import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  PendingActions as PendingActionsIcon,
  Link as LinkIcon,
  Add as AddIcon,
  UploadFile as UploadFileIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { accountingApi, DashboardStats, ContractGrossProfit } from '../services/accounting';
import { useCompany } from '../contexts/CompanyContext';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';

const Dashboard: React.FC = () => {
  const { baseCurrency } = useCompany();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [contractProfit, setContractProfit] = useState<ContractGrossProfit | null>(null);
  const [loadingContracts, setLoadingContracts] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountingApi.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load dashboard data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadContractProfit = useCallback(async () => {
    try {
      setLoadingContracts(true);
      const data = await accountingApi.getContractGrossProfit();
      setContractProfit(data);
    } catch (err) {
      console.error('Failed to load contract profit:', err);
    } finally {
      setLoadingContracts(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    loadContractProfit();
  }, [loadDashboardData, loadContractProfit]);

  const getRevenueChange = () => {
    if (!stats) return 0;
    if (stats.lastMonth.revenue === 0) return stats.thisMonth.revenue > 0 ? 100 : 0;
    return ((stats.thisMonth.revenue - stats.lastMonth.revenue) / stats.lastMonth.revenue) * 100;
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color?: 'primary' | 'success' | 'error' | 'warning' | 'info';
    change?: number;
    subtitle?: string;
  }> = ({ title, value, icon, color = 'primary', change, subtitle }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
            {change !== undefined && (
              <Box display="flex" alignItems="center" mt={1}>
                {change >= 0 ? (
                  <TrendingUpIcon fontSize="small" color="success" />
                ) : (
                  <TrendingDownIcon fontSize="small" color="error" />
                )}
                <Typography
                  variant="caption"
                  color={change >= 0 ? 'success.main' : 'error.main'}
                  sx={{ ml: 0.5 }}
                >
                  {Math.abs(change).toFixed(1)}% vs last month
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) return null;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* Quick Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/invoices/create')}
          >
            Create Invoice
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate('/expenses')}
          >
            Add Expense
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => navigate('/bank')}
          >
            Upload Bank Statement
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => navigate('/accounting')}
          >
            View Reports
          </Button>
        </Box>
      </Paper>

      {/* Key Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Clients"
            value={stats.totals.clients}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Invoices"
            value={stats.totals.invoices}
            icon={<ReceiptIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="This Month Revenue"
            value={formatCurrency(stats.thisMonth.revenue, baseCurrency)}
            icon={<TrendingUpIcon />}
            color="success"
            change={getRevenueChange()}
            subtitle={`Profit: ${formatCurrency(stats.thisMonth.profit, baseCurrency)}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Outstanding"
            value={formatCurrency(stats.outstanding.amount, baseCurrency)}
            icon={<AccountBalanceIcon />}
            color="warning"
            subtitle={`${stats.outstanding.count} invoices`}
          />
        </Grid>
      </Grid>

      {/* Financial Overview & Alerts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              This Month Financial Overview
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Revenue
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(stats.thisMonth.revenue, baseCurrency)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Expenses
                </Typography>
                <Typography variant="h6" color="error.main">
                  {formatCurrency(stats.thisMonth.expenses + stats.thisMonth.payroll + (stats.thisMonth.depreciation || 0), baseCurrency)}
                </Typography>
                {stats.thisMonth.depreciation && stats.thisMonth.depreciation > 0 && (
                  <Typography variant="caption" color="textSecondary">
                    (incl. {formatCurrency(stats.thisMonth.depreciation, baseCurrency)} depreciation)
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Net Profit
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={stats.thisMonth.profit >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(stats.thisMonth.profit, baseCurrency)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Profit Margin
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={stats.thisMonth.profitMargin >= 0 ? 'success.main' : 'error.main'}
                >
                  {stats.thisMonth.profitMargin.toFixed(1)}%
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pending Items
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              {stats.pending.timesheets > 0 && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <PendingActionsIcon color="warning" />
                    <Typography variant="body1">Timesheets Pending Approval</Typography>
                  </Box>
                  <Chip
                    label={stats.pending.timesheets}
                    color="warning"
                    onClick={() => navigate('/approvals')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              )}
              {stats.pending.expenses > 0 && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <PendingActionsIcon color="warning" />
                    <Typography variant="body1">Expenses Pending Approval</Typography>
                  </Box>
                  <Chip
                    label={stats.pending.expenses}
                    color="warning"
                    onClick={() => navigate('/approvals')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              )}
              {stats.unreconciled.transactions > 0 && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinkIcon color="info" />
                    <Typography variant="body1">Unreconciled Transactions</Typography>
                  </Box>
                  <Chip
                    label={stats.unreconciled.transactions}
                    color="info"
                    onClick={() => navigate('/bank')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              )}
              {stats.pending.total === 0 && stats.unreconciled.transactions === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No pending items
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Contract Gross Profit */}
      {contractProfit && contractProfit.contracts.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Contract Gross Profit
          </Typography>
          <Divider sx={{ my: 2 }} />
          {loadingContracts ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {contractProfit.contracts.map((contract) => (
                <Grid item xs={12} md={6} key={contract.clientId}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {contract.clientName}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Revenue
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            {formatCurrency(contract.revenue, contract.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Cost
                          </Typography>
                          <Typography variant="body2" color="error.main">
                            {formatCurrency(contract.cost, contract.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Gross Profit
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color={contract.grossProfit >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(contract.grossProfit, contract.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Margin
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color={contract.grossProfitMargin >= 0 ? 'success.main' : 'error.main'}
                          >
                            {contract.grossProfitMargin.toFixed(1)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="textSecondary">
                            {contract.totalHours.toFixed(1)} hours • {contract.invoiceCount} invoices
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Invoices
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {stats.recent.invoices.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No recent invoices
              </Typography>
            ) : (
              <List dense>
                {stats.recent.invoices.map((invoice, idx) => (
                  <React.Fragment key={invoice.id}>
                    <ListItem
                      sx={{ px: 0, cursor: 'pointer' }}
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {invoice.invoiceNumber}
                            </Typography>
                            <Chip
                              label={invoice.status}
                              size="small"
                              color={
                                invoice.status === 'paid'
                                  ? 'success'
                                  : invoice.status === 'sent'
                                  ? 'info'
                                  : 'default'
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" component="span">
                              {invoice.clientName}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="textSecondary">
                              {format(parseISO(invoice.issueDate), 'MMM d, yyyy')} •{' '}
                              {formatCurrency(invoice.total, invoice.currency)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {idx < stats.recent.invoices.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
            <Box mt={2}>
              <Button size="small" onClick={() => navigate('/invoices')}>
                View All Invoices
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Expenses
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {stats.recent.expenses.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No recent expenses
              </Typography>
            ) : (
              <List dense>
                {stats.recent.expenses.map((expense, idx) => (
                  <React.Fragment key={expense.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '60%' }}>
                              {expense.description}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {formatCurrency(expense.amount, expense.currency)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" color="textSecondary">
                              {format(parseISO(expense.expenseDate), 'MMM d, yyyy')}
                            </Typography>
                            {expense.status && (
                              <>
                                {' • '}
                                <Chip
                                  label={expense.status}
                                  size="small"
                                  color={
                                    expense.status === 'approved'
                                      ? 'success'
                                      : expense.status === 'rejected'
                                      ? 'error'
                                      : 'default'
                                  }
                                />
                              </>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    {idx < stats.recent.expenses.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
            <Box mt={2}>
              <Button size="small" onClick={() => navigate('/expenses')}>
                View All Expenses
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Payments
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {stats.recent.payments.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No recent payments
              </Typography>
            ) : (
              <List dense>
                {stats.recent.payments.map((payment, idx) => (
                  <React.Fragment key={payment.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {payment.invoiceNumber || 'Payment'}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(payment.amount, payment.currency || baseCurrency)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="textSecondary">
                            {payment.clientName && `${payment.clientName} • `}
                            {format(parseISO(payment.paymentDate), 'MMM d, yyyy')}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {idx < stats.recent.payments.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
            <Box mt={2}>
              <Button size="small" onClick={() => navigate('/payments')}>
                View All Payments
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
